import { useState } from "react";
import { Archive, Copy, Download, FileText, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  contentDestinationFormatLabels,
  getPublicationWorkflow,
  isPublicationOutdated,
  type ContentBrief,
  type ContentBriefDraft,
  type ContentBriefPublication,
  type ContentPublishingConnector,
} from "@/lib/composerCore";

type Props = {
  briefs: ContentBrief[];
  drafts: ContentBriefDraft[];
  publications: ContentBriefPublication[];
  connectors: ContentPublishingConnector[];
  canDispatch: boolean;
  onPromote: (pub: ContentBriefPublication, bucket: "primary" | "secondary") => void;
  /**
   * Dispatch one publication to one connector.
   * When `options.silent` is true the caller will surface its own aggregate toast.
   * Returns true when the dispatch did NOT fail (succeeded or queued).
   */
  onSend: (
    pub: ContentBriefPublication,
    connectorId: string,
    options?: { silent?: boolean },
  ) => Promise<boolean> | void;
  onArchive: (pub: ContentBriefPublication) => void;
  onCopy: (pub: ContentBriefPublication) => void;
  onDownload: (pub: ContentBriefPublication) => void;
};

type SendPopoverProps = {
  pub: ContentBriefPublication;
  connectors: ContentPublishingConnector[];
  canDispatch: boolean;
  onSend: Props["onSend"];
};

const SendPopover = ({ pub, connectors, canDispatch, onSend }: SendPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = connectors.length > 0 && selected.size === connectors.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(connectors.map((c) => c.id)));
  };

  const handleSend = async () => {
    if (selected.size === 0) return;
    setSending(true);
    let ok = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        const result = await onSend(pub, id, { silent: true });
        if (result === false) failed += 1;
        else ok += 1;
      } catch {
        failed += 1;
      }
    }
    setSending(false);
    setOpen(false);
    setSelected(new Set());
    if (failed === 0) {
      toast.success(`Sent to ${ok} destination${ok === 1 ? "" : "s"}`);
    } else if (ok === 0) {
      toast.error(`Send failed for ${failed} destination${failed === 1 ? "" : "s"}`);
    } else {
      toast.warning(`Sent to ${ok}, failed ${failed}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-xs"
          disabled={!canDispatch || connectors.length === 0}
        >
          <Send className="h-3.5 w-3.5" /> Send
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2" align="end">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium">Send to destinations</p>
          {connectors.length > 1 ? (
            <button
              type="button"
              className="text-[11px] text-primary hover:underline"
              onClick={toggleAll}
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          ) : null}
        </div>
        {connectors.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No active connectors. Configure one in Workspace settings.
          </p>
        ) : (
          <>
            <div className="space-y-1">
              {connectors.map((c) => {
                const id = `send-${pub.id}-${c.id}`;
                return (
                  <label
                    key={c.id}
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-accent"
                  >
                    <Checkbox
                      id={id}
                      checked={selected.has(c.id)}
                      onCheckedChange={() => toggle(c.id)}
                    />
                    <span className="truncate">{c.display_name}</span>
                  </label>
                );
              })}
            </div>
            <Button
              size="sm"
              className="w-full"
              disabled={selected.size === 0 || sending}
              onClick={() => void handleSend()}
            >
              {sending
                ? "Sending…"
                : `Send to ${selected.size || ""}${selected.size ? " destination" : "destinations"}${selected.size > 1 ? "s" : ""}`.trim()}
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const PublishingOutbox = ({
  briefs,
  drafts,
  publications,
  connectors,
  canDispatch,
  onPromote,
  onSend,
  onArchive,
  onCopy,
  onDownload,
}: Props) => {
  const briefById = new Map(briefs.map((b) => [b.id, b]));
  const draftById = new Map(drafts.map((d) => [d.id, d]));
  const activeConnectors = connectors.filter((c) => c.status === "active");

  const outboxPubs = publications.filter(
    (p) => p.status === "succeeded" && getPublicationWorkflow(p).state === "outbox",
  );

  const grouped = new Map<string, ContentBriefPublication[]>();
  for (const pub of outboxPubs) {
    const arr = grouped.get(pub.piece_id) ?? [];
    arr.push(pub);
    grouped.set(pub.piece_id, arr);
  }

  return (
    <Card className="shadow-soft">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-primary" /> Outbox · Ready to act on
        </CardTitle>
        <CardDescription>
          Prepared outputs awaiting a decision. Nothing is deleted automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {outboxPubs.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Nothing waiting. Open a brief and run Publishing prep.
          </div>
        ) : (
          Array.from(grouped.entries()).map(([briefId, pubs]) => {
            const brief = briefById.get(briefId);
            return (
              <div key={briefId} className="space-y-2 rounded-md border border-border bg-background/40 p-3">
                <p className="text-sm font-medium">{brief?.title ?? "Untitled brief"}</p>
                <div className="space-y-2">
                  {pubs.map((pub) => {
                    const draft = draftById.get(pub.draft_id);
                    const stale = brief && draft ? isPublicationOutdated(brief, draft, pub) : false;
                    return (
                      <div
                        key={pub.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card p-2 text-sm"
                      >
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <Badge variant="outline">{contentDestinationFormatLabels[pub.destination_format]}</Badge>
                          {stale ? <Badge variant="outline">Stale</Badge> : null}
                          <span className="truncate text-xs text-muted-foreground">
                            {pub.prepared_title || "Prepared output"}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(pub.updated_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
                                <Sparkles className="h-3.5 w-3.5" /> Promote to Corpus
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 space-y-2" align="end">
                              <p className="text-xs font-medium">Add to which bucket?</p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => onPromote(pub, "primary")}
                                >
                                  Primary
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => onPromote(pub, "secondary")}
                                >
                                  Secondary
                                </Button>
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                Creates a new entry in this workspace's Corpus.
                              </p>
                            </PopoverContent>
                          </Popover>

                          <SendPopover
                            pub={pub}
                            connectors={activeConnectors}
                            canDispatch={canDispatch}
                            onSend={onSend}
                          />

                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onCopy(pub)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => onDownload(pub)}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => onArchive(pub)}
                          >
                            <Archive className="h-3.5 w-3.5" /> Archive
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
